import { Button, Card, Checkbox, Col, Divider, Form, Input, message, Modal, Radio, Row, Space, Spin, Table, Tabs, Tooltip, Typography } from "antd";

import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { PayPalButton } from "react-paypal-button-v2";
import ReCAPTCHA from "react-google-recaptcha";
import { useMediaQuery } from "react-responsive";
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import PromoCodes from "../components/components/checkout/PromoCodes";
import ShippingProvider from "../components/components/newcheckout/ShippingProvider";
import CustomForm from "../components/components/checkout/CustomForm";
import GiftCardPurchase from "../components/components/giftcards/GiftCardPurchase";
import ShippingForm from "../components/components/checkout/ShippingForm";
import PhoneNumberFormatter from "../components/formatters/PhoneNumberFormatter";

import CreditCardWrapper from "../components/components/checkout/CreditCardWrapper";
import ClearentPaymentsForm from "../components/components/checkout/ClearentPaymentsForm";
import GlobalWrapper from "../components/components/checkout/GlobalWrapper";
import SiteFooter2 from "../components/SiteFooter2";

import { getPaymentButton } from "../components/components/checkout/PaymentChoices";
import { apiCall } from "../components/utilities/Api";
import conditional from "../components/utilities/conditional";
import Html from "../components/utilities/Html";

import { getItemAlt, getTitleImagePath, pluralize } from "../util/Utils";
import { pixelTrack } from "../util/SiteRouter";
import { useSession } from "../util/Session";

import PurchaseEmailChecker from "./PurchaseEmailChecker";
import PurchaseFulfillmentSelector from "./PurchaseFulfillmentSelector";
import PurchaseAdditionalInformation from "./PurchaseAdditionalInformation";
import PurchaseGuestShipping from "./PurchaseGuestShipping";


export const STEPS = {
    EMAIL: 1,
    FULFILLMENT: 2,
    ADDITIONAL: 3,
    CHECKOUT: 4,
    SHIPPING: 4,
    PAYMENT: 4
};


export default function NewPurchaseLayout() {

    const history = useHistory();
    const [session, setSession] = useSession();
    const [form] = Form.useForm();
    const ismobile = useMediaQuery({ query: "(max-width: 716px)" });

    const recaptchaRef = useRef(null);
    const canSubmitRef = useRef(true);

    const loggedInEmail = session?.logged_in ? session?.user?.email_address || "" : "";

    const [status, setStatus] = useState(() => ({
        new_user: false,
        step: loggedInEmail ? STEPS.FULFILLMENT : STEPS.EMAIL,
        completedStep: loggedInEmail ? STEPS.EMAIL : 0,
        email_address: loggedInEmail,
        delivery_preference: "",
        delivery_preference_label: "",
        custom: false,
        address_id: null,
        guest_address_json: "",
        shipping_provider: "",
        shipping_delivery_preference: "",
        multiship_method: "",
        payment_method: "",
        gift_registry_id: null
    }));


    const [isValid, setIsValid] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [choice, setChoice] = useState("");

    const [paymentOptions, setPaymentOptions] = useState({
        ship: [],
        pickup: [],
        electronic: []
    });

    const [loadingShipping, setLoadingShipping] = useState(false);
    const [loadingPayments, setLoadingPayments] = useState(true);
    const [shippingProviders, setShippingProviders] = useState([]);
    const [selected, setSelected] = useState(0);
    const [errorMSG, setErrorMSG] = useState("");
    const [captchaToken, setCaptchaToken] = useState(null);
    const [globalOpen, setGlobalOpen] = useState(false);
    const [ccSubmitCount, setCCSubmitCount] = useState(0);
    const [summaryOpen, setSummaryOpen] = useState(!ismobile);

    // Set default comm pref
    let communication_preference;
    if (session?.logged_in === true && session?.user?.communication_preference === "text" && window.sitesettings?.store_info?.allowed_comm_prefs?.text) {
        communication_preference = "text";
    } else if (session?.logged_in === true && session?.user?.communication_preference !== "text") {
        communication_preference = session?.user?.communication_preference;
    }

    const allowedPrefs = window.sitesettings?.store_info?.allowed_comm_prefs || {};
    const availablePrefs = [allowedPrefs.email ? "email" : null, allowedPrefs.phone ? "phone" : null, (allowedPrefs.text && window.sitesettings?.store_info?.texting_enabled) ? "text" : null].filter(Boolean);
    const defaultPreference = communication_preference || availablePrefs[0] || null;

    const [selectedPref, setSelectedPref] = useState(defaultPreference);

    const instructions_message = window.sitesettings.checkout_info.show_order_inscription ? "Inscription" : "Instructions or comments";

    const {
        data: searchBarData = {}
    } = window.sitesettings.global_content.find(item => item.component === "search_bar") || {};


    // figure out where to pin to the top
    const { logo_height = 40, padding_y = 2 } = searchBarData;
    const affixed = logo_height + padding_y + padding_y + 60;


    // cart empty go away..
    useEffect(() => {
        if (!session?.cart?.items?.length) {
            history.replace("/checkout/cart");
        }
    }, [session?.cart?.items?.length]);


    // Reset on login / logout
    useEffect(() => {

        const email = session?.logged_in ? session?.user?.email_address || "" : "";

        setStatus(prev => ({
            ...prev,
            new_user: false,
            step: session?.logged_in ? STEPS.FULFILLMENT : STEPS.EMAIL,
            completedStep: session?.logged_in ? STEPS.EMAIL : 0,
            email_address: email,
            delivery_preference: "",
            delivery_preference_label: "",
            custom: false,
            address_id: null,
            guest_address_json: "",
            payment_method: false
        }));

        form.resetFields();

        if (email) {
            form.setFieldsValue({
                email,
                email_address: email
            });
        }

        setShippingProviders([]);
        setSelected(0);
        setChoice("");
        setIsValid(false);

    }, [session?.logged_in]);

    // scroll back when you change the step
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    }, [status.step]);

    // update the email
    const updateEmail = (emailAddress, new_user = false) => {

        form.setFieldsValue({
            email: emailAddress,
            email_address: emailAddress
        });

        setStatus(prev => ({
            ...prev,
            email_address: emailAddress,
            new_user,
            payment_method: "",
            step: STEPS.FULFILLMENT,
            completedStep: Math.max(prev.completedStep, STEPS.EMAIL)
        }));
    };

    // update delivery
    const updateFulfillment = ({ delivery_preference, custom, label }) => {

        const guestShipping = !session?.logged_in && (delivery_preference === "ship" || delivery_preference === "local_delivery");
        const isShipping = delivery_preference === "ship" || delivery_preference === "local_delivery";

        form.setFieldsValue({
            address_id: null,
            shipping_provider: "",
            shipping_label: "",
            multiship_method: "",
            payment_method: ""
        });

        setShippingProviders([]);
        setSelected(0);
        setChoice("");
        setIsValid(false);

        const preferred = session?.logged_in ? session?.user?.addresses?.find(item => item?.preferred) : null;
        const address_id = preferred?.address_id || null;


        setStatus(prev => ({
            ...prev,
            delivery_preference,
            delivery_preference_label: label,
            custom: Boolean(custom),
            address_id,
            guest_address_json: "",
            shipping_provider: "",
            shipping_delivery_preference: "",
            multiship_method: "",
            payment_method: "",
            step: guestShipping ? STEPS.ADDITIONAL : STEPS.CHECKOUT,
            completedStep: Math.max(prev.completedStep, STEPS.FULFILLMENT)
        }));

        if (session?.logged_in && isShipping && address_id) {
            getShippingProviders(address_id);
        }

    };

    // Guest shipping (need to refactor...)
    const updateAdditionalInformation = submitted => {

        const submittedValues = submitted?.values ? submitted.values : submitted || {};
        const values = { ...form.getFieldsValue(true), ...submittedValues };

        const guestAddress = {
            name: values.name || "",
            company_name: values.company_name || "",
            phone_number: values.phone_number || "",
            phone_number_ext: values.phone_number_ext || "",
            email_address: values.email_address || values.email || status.email_address || "",
            street_address: values.street_address || "",
            street_address_2: values.street_address_2 || "",
            city: values.city || "",
            province: values.province || "",
            country: values.country || "",
            postal_code: values.postal_code || ""
        };

        const guest_address_json = JSON.stringify(guestAddress);

        form.setFieldsValue(values);

        setStatus(prev => ({
            ...prev,
            ...values,
            address_id: null,
            guest_address_json,
            shipping_provider: "",
            shipping_delivery_preference: "",
            multiship_method: "",
            payment_method: "",
            step: STEPS.CHECKOUT,
            completedStep: Math.max(prev.completedStep, STEPS.ADDITIONAL)
        }));

        // load shipping providers using guest info
        if (status.delivery_preference === "ship" || status.delivery_preference === "local_delivery") {
            getShippingProviders(null, guest_address_json);
        }
    };


    // sanity check
    useEffect(() => {

        const cart = session?.cart;

        if (!cart) return;

        if (cart.contains_unavailable) {
            history.replace("/checkout/cart");
            return;
        }

        if (cart.contains_unshippable && status.delivery_preference === "ship") {

            history.replace("/checkout/cart");
            return;
        }

        const electronicOnly = cart?.summary?.contains_only_emailed_gift_card;

        if (electronicOnly && status.delivery_preference && status.delivery_preference !== "electronic") {
            history.replace("/checkout/cart");
            return;
        }

        if (!electronicOnly && status.delivery_preference === "electronic") {
            history.replace("/checkout/cart");
        }

    }, [session?.cart, status.delivery_preference]);


    // load paypal if store has it
    useEffect(() => {
        const paypalID = window.sitesettings?.checkout_info?.paypal_id;
        if (!paypalID) return;
        // don't double load it
        if (document.querySelector("script[data-checkout-paypal-sdk]")) { return; }

        const script = document.createElement("script");
        script.dataset.checkoutPaypalSdk = "true";
        script.src = "https://www.paypal.com/sdk/js?client-id=" + paypalID + "&currency=" + window.sitesettings.checkout_info.currency;
        script.async = true;
        document.body.appendChild(script);
    }, []);



    const handleCaptchaChange = token => {
        setCaptchaToken(token || null);
        if (!token) return;
        apiCall("checkout/setCaptcha", { captcha: token }, (_s, _r) => {
            if (!_s) {
                message.error(_r.error);
            }
        }
        );
    };


    const drawShippingMessage = () => {
        const shipping_message = window.sitesettings.checkout_info.shipping_message;
        if (status.delivery_preference === "ship" && shipping_message) {
            return (<><div className="shim" /><div style={{ padding: 30, border: "1px dashed #ddd" }} ><Html clear={false} className="html_text" html={shipping_message} /></div></>)
        }
        return (<></>)
    }

    const drawLocalMessage = () => {
        const local_message = window.sitesettings.checkout_info.local_delivery_message;
        if (status.delivery_preference === "ship" && local_message) {
            return (<><div className="shim" /><div style={{ padding: 30, border: "1px dashed #ddd" }} ><Html clear={false} className="html_text" html={local_message} /></div></>)
        }
        return (<></>)
    }

    // validation scroll
    const scrollToElement = (elementID, offset = affixed + 40) => {
        const element = document.getElementById(elementID);
        if (!element) return;
        const position = element.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
            top: position - offset,
            behavior: "smooth"
        });
    };


    const validateForm = async (trigger = false, paymentMethod = status.payment_method) => {
        if (!status.payment_method && !trigger) { return; }

        try {
            await form.validateFields();
            setIsValid(Boolean(paymentMethod));
            if (trigger && ismobile) {
                scrollToElement(
                    "end",
                    affixed + 50
                );

                  scrollToElement("checkout-submit", affixed + 20);
            }
        } catch (error) {

            if (error?.outOfDate && !error?.errorFields?.length) {
                setIsValid(Boolean(paymentMethod));
                return;
            }

            setIsValid(false);

            if (trigger && error?.errorFields?.length) {

                const first = error.errorFields[0]?.name?.[0];
                if (first) {
                    scrollToElement(first);
                }
                message.error(
                    <span
                        role="alert"
                        aria-live="assertive"
                    >
                        Missing required field.
                        Please check your input and try again.
                    </span>
                );
            }
        }
    };


    const getPaymentChoices = () => {
        setLoadingPayments(true);

        setPaymentOptions({
            ship: [],
            pickup: [],
            electronic: []
        });

        apiCall("checkout/getPaymentChoices", {}, (ok, result) => {
            if (ok) {
                setPaymentOptions(result);
            }
            setLoadingPayments(false);
        }
        );
    };


    useEffect(() => {
        getPaymentChoices();
    }, [session?.cart?.summary?.subtotal, session?.cart?.summary?.total_gift_card_amount_used]);


    // clear if address or total changes
    useEffect(() => {

        setStatus(prev => ({
            ...prev,
            payment_method: false
        }));

        form.setFieldsValue({
            payment_method: ""
        });

        setChoice("");
        setIsValid(false);

    }, [session?.cart?.summary?.subtotal, status.address_id, status.guest_address_json]);


    const hashCode = string => {
        let hash = 0;
        if (!string) return hash;
        for (let i = 0; i < string.length; i++) {
            const char = string.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return hash;
    };


    const selectProvider = (hash, provider, delivery_preference, batch = "all", guest_address_json = "", address_id = null) => {
        const payload = {
            delivery_preference,
            provider,
            multiship_method: batch
        };

        const guestAddress = guest_address_json || status.guest_address_json;

        if (!session?.logged_in && guestAddress) {
            payload.guest_address_json = guestAddress;
        } else {
            payload.address_id = address_id ?? form.getFieldValue("address_id") ?? status.address_id;
        }

        apiCall("checkout/setShipping", payload, (ok, result) => {

            if (!ok) return;

            setSession({ ...session, cart: result });
            setSelected(hash);
            const shipping_provider = delivery_preference === "local_delivery" ? "local_delivery" : provider;

            form.setFieldsValue({
                shipping_provider,
                shipping_label: "true",
                multiship_method: batch,
                payment_method: ""
            });

            setStatus(prev => ({
                ...prev,
                shipping_provider,
                shipping_delivery_preference:
                    delivery_preference,
                multiship_method: batch,
                payment_method: ""
            }));
        }
        );
    };


    const selectDefaultShipping = (providers, guest_address_json = "", address_id = null) => {

        const normal = providers.filter(item => item.available && item.delivery_preference !== "local_delivery").sort((a, b) => parseFloat(a.cost) - parseFloat(b.cost));

        if (normal.length) {
            const item = normal[0];
            selectProvider(hashCode(item.provider + item.delivery_preference), item.provider, item.delivery_preference, "all", guest_address_json, address_id);
            return;
        }
        const local = providers.find(item => item.available && item.delivery_preference === "local_delivery");
        if (local) {
            selectProvider(hashCode(local.provider + local.delivery_preference), local.provider, local.delivery_preference, "all", guest_address_json, address_id);
        }
    };


    const getShippingProviders = (address_id = null, guest_address_json = "") => {

        setLoadingShipping(true);
        setShippingProviders([]);

        const payload = {};

        if (!session?.logged_in && (guest_address_json || status.guest_address_json)) {
            payload.guest_address_json = guest_address_json || status.guest_address_json;
        } else {
            payload.address_id = address_id ?? status.address_id ?? form.getFieldValue("address_id");
        }

        apiCall("checkout/getShippingProviders", payload, (_s, _r) => {

            if (_s) {
                setShippingProviders(_r || []);
                selectDefaultShipping(_r || [], guest_address_json, address_id);
            } else {
                form.setFieldsValue({ shipping_provider: "", shipping_label: "" });
            }
            setLoadingShipping(false);
            validateForm();
        }
        );
    };


    // watch address
    const watchedAddressID = Form.useWatch("address_id", form);

    useEffect(() => {

        if (status.step !== STEPS.CHECKOUT) { return; }

        if (!session?.logged_in) { return; }

        if (status.delivery_preference !== "ship" && status.delivery_preference !== "local_delivery") {
            return;
        }

        if (watchedAddressID === undefined || watchedAddressID === null) {
            return;
        }

        if (watchedAddressID === status.address_id) {
            return;
        }

        setStatus(prev => ({
            ...prev,
            address_id: watchedAddressID,
            payment_method: false
        }));

        getShippingProviders(watchedAddressID);

    }, [watchedAddressID, status.step, session?.logged_in, status.delivery_preference, status.guest_address_json]);


    const drawBillingShipping = () => {

        if (!session?.logged_in) {
            return null;
        }

        return (
            <div id="shipping">
                <ShippingForm
                    state={status}
                    callback={addresses => {
                        setSession({
                            ...session,
                            user: { ...session.user, addresses }
                        });
                        getShippingProviders(status.address_id);
                    }}

                    setState={next => {
                        setStatus(prev => ({
                            ...prev,
                            address_id: next.address_id,
                            payment_method: ""
                        }));
                        getShippingProviders(next.address_id);
                    }}

                    hideCard={true}
                    setLoading={() => { }}
                    setShipping={() => { }}
                    errorMSG=""
                    setErrorMSG={() => { }}
                    form={form}
                    delivery={status.delivery_preference}
                    setSaved={() => { }}
                    saved={{}}
                />
            </div>
        );
    };


    const drawShipping = () => {

        if (loadingShipping) {
            return (<Spin><div style={{ minHeight: 80 }} /></Spin>);
        }

        if (shippingProviders.length === 0) {

            return (
                <Card style={{ borderColor: "#f12a46" }} >
                    <span style={{ color: "#f12a46" }} >
                        No shipping available for
                        the selected address
                    </span>
                </Card>
            );
        }

        return shippingProviders.map(item => {
            const hash = hashCode(item.provider + item.delivery_preference);
            return (
                <div key={hash}>
                    <ShippingProvider
                        autoselect={false}
                        saved={{}}
                        setSaved={() => { }}
                        address_id={status.address_id}
                        guest_address_json={status.guest_address_json}
                        selectProvider={selectProvider}
                        selected={selected}
                        hash={hash}
                        data={item}
                    />
                    <div className="shim" />
                    <div className="shim" />
                </div>
            );
        }
        );
    };


    const getCustomForm = () => {

        const ci = window.sitesettings.checkout_info;

        let formdata = false;

        switch (status.delivery_preference) {

            case "pickup":

                if ((ci.form_pickup || ci.form_pickup === 0) && !status.custom) {
                    formdata = ci.custom_forms[ci.form_pickup];
                }

                if ((ci.form_custom_pickup || ci.form_custom_pickup === 0) && status.custom) {
                    formdata = ci.custom_forms[ci.form_custom_pickup];
                }

                break;

            case "ship":
            case "local_delivery":

                if ((ci.form_ship || ci.form_ship === 0) && !status.custom) {
                    formdata = ci.custom_forms[ci.form_ship];
                }

                if ((ci.form_custom_ship || ci.form_custom_ship === 0) && status.custom) {
                    formdata = ci.custom_forms[ci.form_custom_ship];
                }

                break;

            default:
                break;
        }

        return formdata || false;
    };

    const customForm = getCustomForm();

    const displayClickCollectForm = () => {

        if (session.logged_in) { return null; }

        if (status.delivery_preference === "ship" || status.delivery_preference === "local_delivery") {
            return null;
        }


        return (
            <>
                <Form.Item
                    label="Your name"
                    name="name"
                    rules={[{ required: true, message: "Please input your name." }]}
                >
                    <Input size="large" placeholder="Your name" />
                </Form.Item>

                <Form.Item
                    label="Phone"
                    name="phone"
                    rules={[{ required: true, message: "Please input your phone number." }]}
                >
                    <PhoneNumberFormatter form={form} country="Canada" style={{ width: 156 }} inputMode="numeric" placeholder="Phone Number" />
                </Form.Item>

            </>
        );
    };


    const drawContact = () => {

        return (
            <Card>
                {displayClickCollectForm()}
                <Form.Item label="Purchase Order # (For School or Institution orders only)" name="promo_code" >
                    <Input size="large" maxLength={10} placeholder="Purchase Order # (optional)" />
                </Form.Item>

                <Form.Item label={instructions_message} name="instructions" >
                    <Input.TextArea size="large" maxLength={1000} rows={5} placeholder={instructions_message} />
                </Form.Item>

                <Form.Item
                    initialValue={defaultPreference}
                    label="Preferred method of communication"
                    name="preferred_communication"
                >
                    <Radio.Group onChange={e => setSelectedPref(e.target.value)} buttonStyle="solid" >
                        {allowedPrefs.email && (
                            <Radio.Button value="email" > Email </Radio.Button>
                        )}
                        {allowedPrefs.phone && (
                            <Radio.Button value="phone" > Phone </Radio.Button>
                        )}
                        {(allowedPrefs.text && window.sitesettings.store_info.texting_enabled) && (
                            <Radio.Button value="text" > Text </Radio.Button>
                        )}
                    </Radio.Group>
                </Form.Item>


                {selectedPref === "text" && (
                    <>

                        <Form.Item
                            name="agreement"
                            valuePropName="checked"
                            rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error("Agree for SMS communications, or select a different method of communication above to proceed")) }]}
                        >
                            <Checkbox>
                                <em>
                                    I agree to receive text messages from{" "} {window?.sitesettings?.store_info?.name}. <br /> I agree to receive up to 3 messages per order, with updates on the order's status. <br /> I understand that message frequency will increase if additional orders are placed. <br /> Message &amp; data rates may apply. Reply{" "} <strong> STOP </strong>{" "} to unsubscribe. Reply{" "} <strong> HELP </strong>{" "} for help.
                                </em>
                            </Checkbox>
                        </Form.Item>

                        <Space split={<>|</>}>
                            <SiteFooter2 only_privacy_policy />
                            {window?.sitesettings?.store_info?.privacy_policy_url && (
                                <a style={{ color: "blue" }} target="_blank" rel="noreferrer" href={window.sitesettings.store_info.privacy_policy_url} > Privacy Policy </a>
                            )}
                        </Space>
                    </>
                )}
                <CustomForm formdata={customForm} saved={{}} form={form} />
            </Card>
        );
    };


    const drawEmailGiftCardsDisclaimer = () => {
        return (
            <div>
                <div> <strong> {window.sitesettings.store_info.giftcard_emailed_label} </strong> </div>
                <Divider dashed />
                <Html className="" html={window.sitesettings.store_info.giftcard_emailed_message} />
            </div>
        );
    };


    const drawNotice = () => {

        const checkoutMessage = window.sitesettings.checkout_info.checkout_message;
        const containsEmailedGiftCard = session.cart.summary.contains_emailed_gift_card;

        if (status.delivery_preference !== "electronic" && session.logged_in && (!checkoutMessage || checkoutMessage === "<br>") && !containsEmailedGiftCard) {
            return null;
        }

        if (status.delivery_preference === "electronic") {
            return (
                <div style={{ padding: 30, border: "1px dashed #ddd" }} >
                    {drawEmailGiftCardsDisclaimer()}
                </div>
            );
        }

        return (
            <>
                <div className="shim" />
                <div style={{ padding: 30, border: "1px dashed #ddd" }} >
                    {checkoutMessage && (
                        <Html clear={false} className="" html={checkoutMessage} />
                    )}

                    {containsEmailedGiftCard && (
                        <>
                            <Divider dashed />
                            {drawEmailGiftCardsDisclaimer()}
                        </>
                    )}
                </div>
            </>
        );
    };

    const displayChoices = () => {

        if (!status.delivery_preference) {
            return null;
        }


        let type = (status.delivery_preference === "local_delivery" || status.delivery_preference === "custom_ship") ? "ship" : status.delivery_preference;


        if (status.delivery_preference === "custom_pickup") {
            type = "pickup";
        }

        const options = paymentOptions[type] || [];

        return options.map((item, index) => {

            const disabled = session.cart.summary.total === "0.00" && item !== "not_required";

            return (
                <div key={item} style={{ opacity: disabled ? 0.5 : 1 }} >
                    <span onClick={() => {
                        if (disabled) { return; }
                        form.setFieldsValue({ payment_method: item });
                        setStatus(prev => ({ ...prev, payment_method: item }));
                        setChoice(item);
                        validateForm(true, item);
                    }}
                    >
                        {getPaymentButton(item, index, choice === item ? "selected" : "default", disabled)}
                    </span>
                    <div className="shim" />
                </div>
            );
        }
        );
    };


    const drawPayment = () => {

        if (loadingPayments) {
            return (
                <Spin>
                    <div style={{ minHeight: 80 }} />
                </Spin>
            );
        }


        return (
            <div>
                <Divider orientation="left">
                    <big> <strong> Select Payment </strong> </big>
                </Divider>
                <Form.Item hidden rules={[{ required: true }]} name="payment_method" >
                    <Input />
                </Form.Item>
                <Form.Item hidden name="transaction_data" >
                    <Input />
                </Form.Item>
                <Form.Item hidden name="encrypted_cc_info" >
                    <Input />
                </Form.Item>
                <Form.Item hidden name="paypal_order_id" >
                    <Input />
                </Form.Item>
                {displayChoices()}
                <conditional.true value={session.cart.summary.is_shipping_tbd} >
                    <Card>
                        <strong> Heads up! </strong>
                        <p> Shipping charges still need to be calculated for your order. We will contact you shortly to confirm the cost to ship before finalizing any additional payments. </p>
                    </Card>
                </conditional.true>
            </div>
        );
    };


    const showUnavailableItems = () => {

        apiCall("session/get", {}, (ok, result) => {

            if (!ok) {
                message.error(result.error);
                return;
            }

            Modal.error({
                icon: <></>,
                title: (
                    <span style={{ color: "#f00" }} > Cart contains unavailable item(s) </span>
                ),
                maskClosable: false,
                content: (
                    <> One or more items became unavailable while you were checking out. Please review your cart and try again. </>
                ),
                okText: "Try again",
                onOk: () => setSession(result)
            });
        }
        );
    };


    const submit = values => {

        if (!canSubmitRef.current) { return; }

        canSubmitRef.current = false;
        setSubmitLoading(true);
        setErrorMSG("");

        const custom_fields = Object.fromEntries(Object.entries(values).filter(([key]) => key.startsWith("cfe")));

        const obj = {
            ...custom_fields,
            custom: status.custom,
            delivery_preference: values.shipping_provider === "local_delivery" ? "local_delivery" : status.delivery_preference,
            preferred_communication: values.preferred_communication || selectedPref || defaultPreference,
            gift_cards: session.cart.hasOwnProperty("gift_cards") ? JSON.stringify(session.cart.gift_cards) : "",
            instructions: values.instructions || "",
            old_promo_code: values.promo_code || "",
            payment_choice: status.payment_method
        };

        if (status.gift_registry_id) {
            obj.gift_registry_id = status.gift_registry_id;
        }

        if (status.payment_method === "credit_card") {
            obj.encrypted_cc_info = values.encrypted_cc_info;
        }

        if (status.delivery_preference === "ship" || status.delivery_preference === "local_delivery") {
            obj.provider = values.shipping_provider;
            obj.multiship_method = values.multiship_method;

            if (!session.logged_in && status.guest_address_json) { obj.guest_address_json = status.guest_address_json; } else {
                obj.address_id = status.address_id;
            }
        }

        // Guest
        if (!session.logged_in) {

            obj.email = status.email_address;

            if (status.delivery_preference !== "ship" && status.delivery_preference !== "local_delivery") {

                if (values.name) {
                    obj.name = values.name;
                }

                if (values.phone) {
                    obj.phone = values.phone;
                }
            }
        }


        // promo
        const promo = session.cart.summary.promo_codes?.find(item => !item.automatic);

        if (promo) {
            obj.promo_code = promo.code;
        }

        // remove empty 
        Object.keys(obj).forEach(
            key => {
                if (obj[key] === "" || obj[key] == null) {
                    delete obj[key];
                }
            }
        );


        const finish = () => {
            setSubmitLoading(false);
            canSubmitRef.current = true;
        };


        const success = result => {
            history.push({ pathname: `/account/order/${result.order_number}`, state: { data: result, is_new: status.new_user, email_address: status.email_address }, search: "success" });
        };


        const fail = result => {
            if (result.error === "Cart contains unavailable items") {
                showUnavailableItems();
                return;
            }
            const error = typeof result.error === "string" ? result.error : `There was an error: ${JSON.stringify(result.error)}`;
            setErrorMSG(error);
            message.error(<span aria-live="assertive"> {error} </span>);
        };

        // clearent & global
        if (status.payment_method === "clearent" || status.payment_method === "global") {

            if (!captchaToken) {
                message.error("Please verify that you are not a robot.");
                finish();
                return;
            }

            if (status.payment_method === "global") {
                obj.postal_code = values.postal_code;
            }

            obj.transaction_data = values.transaction_data;

            apiCall("checkout/cardPayment", { ...obj, captcha: captchaToken }, (_s, _r) => {

                if (_s) {
                    success(_r);
                    finish();
                    return;
                }


                if (_r.error === "Cart contains unavailable items") {
                    showUnavailableItems();
                } else if (ccSubmitCount > 1) {

                    Modal.error({
                        title: "Error",
                        content: (
                            <span aria-live="assertive" > {_r.error} <br /> <br /> Too many failed attempts. Please start over. </span>
                        )
                    });
                    history.push("/checkout/cart");

                } else {

                    if (recaptchaRef.current) {
                        recaptchaRef.current.reset();
                    }
                    setCaptchaToken(null);
                    setGlobalOpen(false);
                    setCCSubmitCount(count => count + 1);

                    Modal.error({
                        title: "Error",
                        content: (<span aria-live="assertive" > {_r.error} </span>)
                    });
                }

                finish();
            }
            );

            return;
        }


        // paypal
        if (status.payment_method === "paypal") {

            obj.order_id = values.paypal_order_id;

            apiCall("checkout/paypalSuccess", obj, (_s, _r) => {

                if (_s) {
                    success(_r);
                } else {

                    Modal.error({
                        title: "There was an error processing your order.",
                        content: (<> No funds were removed from your PayPal account. Please start and complete checkout in a single browser tab. </>),
                        okText: "Start Over",
                        onOk: () => history.push("/checkout/cart")
                    });
                }
                finish();
            });

            return;
        }

        // normal checkout
        apiCall("checkout/submit", obj, (_s, _r) => {
            if (_s) {
                pixelTrack("Purchase", session.cart, obj);
                success(_r);
            } else {
                fail(_r);
            }
            finish();
        }
        );
    };

    const sumTaxes = taxes => {
        return (taxes || []).reduce((total, item) => total + parseFloat(item.amount || 0), 0).toFixed(2);
    };

    const displayPaypalButton = () => {

        return (
            <PayPalButton

                createOrder={(data, actions) => {

                    let totalDiscount = 0;
                    let itemTotal = parseFloat(session.cart.summary.subtotal || 0);
                    const promo = session.cart.summary.promo_codes?.[0];

                    if (promo) {
                        totalDiscount += parseFloat(promo.amount || 0);
                        if (promo.type !== "freeship") {
                            itemTotal += parseFloat(promo.amount || 0);
                        }
                    }

                    totalDiscount += parseFloat(session.cart.summary.post_subtotal_discount || 0);

                    return actions.order.create({

                        application_context: { shipping_preference: "NO_SHIPPING" },
                        purchase_units: [
                            {
                                amount: {
                                    currency_code: window.sitesettings.checkout_info.currency,
                                    value: session.cart.summary.total,
                                    breakdown: {

                                        item_total: {
                                            value: itemTotal.toFixed(2),
                                            currency_code: window.sitesettings.checkout_info.currency
                                        },

                                        tax_total: {
                                            value: sumTaxes(session.cart.summary.taxes),
                                            currency_code: window.sitesettings.checkout_info.currency
                                        },

                                        shipping: {
                                            value: session.cart.summary.shipping,
                                            currency_code: window.sitesettings.checkout_info.currency
                                        },

                                        discount: {
                                            value: totalDiscount.toFixed(2),
                                            currency_code: window.sitesettings.checkout_info.currency
                                        }
                                    }
                                },

                                items: session.cart.items.map(item => ({
                                    name: item.title_info.title,
                                    unit_amount: {
                                        value: item.cart_info.sold_price,
                                        currency_code: window.sitesettings.checkout_info.currency
                                    },
                                    quantity: item.cart_info.quantity
                                })
                                )
                            }
                        ]
                    });
                }}

                amount={session.cart.summary.total}
                shippingPreference="NO_SHIPPING"
                style={{ shape: "rect", color: "gold", layout: "horizontal", label: "paypal" }}

                onApprove={data => {
                    form.setFieldsValue({ paypal_order_id: data.orderID });
                    form.submit();
                }}

                options={{
                    currency: window.sitesettings.checkout_info.currency,
                    clientId: window.sitesettings.checkout_info.paypal_id
                }}
            />
        );
    };

    // finalize button
    const drawSubmit = () => {

        if (!isValid) {

            return (
                <Button disabled type="primary" size="large" block > Complete form &amp; choose payment </Button>
            );
        }


        switch (status.payment_method) {

            case "paypal": return displayPaypalButton();
            case "clearent":
                return (
                    <div>
                        {!captchaToken && (
                            <Form.Item>
                                <ReCAPTCHA ref={recaptchaRef} sitekey="6LfPBUslAAAAAIrLp814HpBIixMj0rL5J3cAxEUw" onChange={handleCaptchaChange} />
                            </Form.Item>
                        )}

                        <div style={{ pointerEvents: captchaToken ? "auto" : "none", opacity: captchaToken ? 1 : 0.5 }} >
                            <ClearentPaymentsForm
                                amount={session.cart.summary.total}
                                onComplete={
                                    transaction_data => {
                                        form.setFieldsValue({
                                            transaction_data
                                        });
                                        form.submit();
                                    }
                                }
                            />
                        </div>
                    </div>
                );


            case "global":

                return (
                    <div>
                        {!captchaToken && (
                            <Form.Item>
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey="6LfPBUslAAAAAIrLp814HpBIixMj0rL5J3cAxEUw"
                                    onChange={handleCaptchaChange}
                                />
                            </Form.Item>
                        )}

                        <Form.Item hidden name="postal_code" >
                            <Input />
                        </Form.Item>

                        <div style={{ pointerEvents: captchaToken ? "auto" : "none", opacity: captchaToken ? 1 : 0.5 }} >
                            <GlobalWrapper
                                open={globalOpen}
                                setOpen={setGlobalOpen}
                                amount={session.cart.summary.total}
                                submitLoading={submitLoading}
                                onComplete={(transaction_data, postal_code) => {
                                    form.setFieldsValue({ postal_code, transaction_data });
                                    form.submit();
                                }}
                            >
                                <Button type="primary" size="large" block > Enter Credit Card </Button>
                            </GlobalWrapper>
                        </div>
                    </div>
                );


            case "credit_card":

                return (
                    <CreditCardWrapper onComplete={
                        encrypted_cc_info => {
                            form.setFieldsValue({ encrypted_cc_info });
                            form.submit();
                        }
                    }>
                        <Button type="primary" size="large" block >
                            Enter Credit Card
                        </Button>
                    </CreditCardWrapper>
                );

            default:
                return (<Button type="primary" size="large" block onClick={() => form.submit()} > Finalize Checkout </Button>);
        }
    };


    // cart summary
    const columns = [

        {
            key: "image",
            width: 45,
            render: (_, row) => {
                const ti = row.title_info;
                return (
                    <img

                        onClick={() => window.open("/item/" + ti.eisbn, "_blank")}
                        onError={event => { event.currentTarget.src = "https://cdn1.bookmanager.com/i/nocover.png"; }}
                        alt={getItemAlt(ti)}
                        style={{ maxHeight: 50, maxWidth: 35, cursor: "pointer" }}
                        src={getTitleImagePath(ti?.eisbn, ti?.cover_image_cache, "large")}
                    />
                );
            }
        },

        {
            key: "title",
            render: (_, row) => {
                const ti = row.title_info;
                return (
                    <Tooltip title={<><div>{ti.title}</div><div><small>{ti.subtitle}</small></div></>}>
                        <div>
                            <div style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} >
                                <a onClick={() => window.open("/item/" + ti.eisbn, "_blank")}> {ti.title} </a>
                                <span><small style={{ opacity: 0.85 }} >{ti.subtitle}</small></span>
                            </div>
                            <div><small>{ti?.binding || ""}</small></div>
                        </div>
                    </Tooltip>
                );
            }
        },


        {
            key: "price",
            width: 100,
            render: (_, row) => (<div> {row?.cart_info?.quantity} {" x $"} {row?.cart_info?.sold_price} </div>)
        }
    ];


    const drawCart = (cart = null, filter = false) => {
        let items = cart || session?.cart?.items || [];
        if (filter) {
            items = items.filter(item => !item.cart_info.cartable);
        }

        return (
            <Table
                className="cart-table"
                size="small"
                pagination={{ pageSize: 3, style: { marginBottom: 0 } }}
                showHeader={false}
                scroll={{ y: 210 }}
                columns={columns}
                dataSource={items}
                rowKey={row => row.cart_info?.id || row.title_info?.eisbn || row.title_info?.title}
            />
        );
    };


    const drawPromoLineItems = (_arr, show_ship = false) => {
        if (!_arr) {
            return (<></>);
        }
        return _arr.map((item, index) => {
            if (((show_ship) && item.type !== "freeship") || (!show_ship) && item.type === "freeship") {
                return (<></>);
            }
            return (
                <Row>
                    <Col span={17}><span style={{ "color": "#e70d3d" }}><small>{item.description}</small></span></Col>
                    <Col span={7}><span style={{ "color": "#e70d3d", "textAlign": "right", "display": "block" }}><small>- ${item.amount}</small></span></Col>
                </Row>
            )
        })
    }


    const drawTaxes = (_arr) => {
        return _arr.map((item, index) => {
            return (
                <Row key={item}>
                    <Col span={17}><small>{(item.type) ? <>{item.type} </> : ""}</small></Col>
                    <Col span={7}><span style={{ "textAlign": "right", "display": "block" }}><small>{"$" + item.amount}</small></span></Col>
                </Row>
            )
        })
    }


    const title_items = session?.cart?.summary ? (session.cart.summary.item_count + " " + pluralize(session.cart.summary.item_count, "title", "s") + ", " + session.cart.summary.total_quantity + " " + pluralize(session.cart.summary.total_quantity, "item", "s")) : "";


    const toggleSummary = () => {
        setSummaryOpen(prev => {
            const opening = !prev;

            if (ismobile) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        window.scrollBy({
                            top: opening ? 150 : -150,
                            behavior: "smooth",
                        });
                    });
                });
            }

            return opening;
        });
    };


    const drawPromoAndGC = () => {



        return (
            <div>
                <div className="shim" /><div className="shim" />
                <Card bodyStyle={{ "paddingTop": "0px" }}>
                    <div>
                        <Divider orientation="left" orientationMargin={0} ><big><strong style={{ "fontSize": "14px" }}>Promo Code</strong></big></Divider>
                        <PromoCodes />
                    </div>


                </Card>
                {(window.sitesettings.store_info.giftcards_enabled && status.step === STEPS.CHECKOUT &&

                    <>
                        <div className="shim" /><div className="shim" />
                        <Card bodyStyle={{ "paddingTop": "0px" }}>
                            <conditional.true value={(window.sitesettings.store_info.giftcards_enabled && status.step === STEPS.CHECKOUT)}>
                                <div><GiftCardPurchase /></div>
                            </conditional.true>
                        </Card>
                    </>
                )}
            </div>
        )
    }

    const drawSummary = () => {
        const summary = session.cart.summary;

        const titleItems = `${summary.item_count} ${pluralize(
            summary.item_count,
            "title",
            "s"
        )}, ${summary.total_quantity} ${pluralize(
            summary.total_quantity,
            "item",
            "s"
        )}`;

        return (
            <div
                style={{
                    position: "sticky",
                    top: affixed,
                    padding: ismobile ? 0 : 20,
                    margin: "0 -20",
                }}
            >


                <Card
                    title={
                        <div
                            onClick={toggleSummary}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: "pointer",
                            }}
                        >
                            <div>
                                <strong style={{ fontSize: 18 }}>
                                    Order Summary
                                </strong>

                                <div style={{ fontSize: 12 }}>
                                    {titleItems}
                                </div>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                }}
                            >
                                <strong style={{ fontSize: 16 }}>
                                    ${summary.total}
                                </strong>

                                <small>
                                    {summaryOpen
                                        ? <UpOutlined />
                                        : <DownOutlined />
                                    }
                                </small>
                            </div>
                        </div>
                    }
                >
                    {summaryOpen && (
                        <>
                            {/* Cart Items */}

                            <div className={(session?.cart?.items?.length > 3) ? "cart-table-fade" : ""}>
                                <Table
                                    size="small"
                                    scroll={{ y: 200 }}
                                    pagination={{
                                        pageSize: 50,
                                        showSizeChanger: false,
                                        hideOnSinglePage: true,
                                        position: ["bottomCenter"],
                                    }}
                                    showHeader={false}
                                    rowKey={row =>
                                        row.cart_info?.id ||
                                        row.title_info?.eisbn ||
                                        row.title_info?.title
                                    }
                                    columns={columns}
                                    dataSource={session.cart.items || []}
                                />
                            </div>


                            <Divider style={{ margin: "14px 0" }} />

                            {/* Price Breakdown */}

                            {summary.original_price && (
                                <Row>
                                    <Col span={17}>Price</Col>
                                    <Col
                                        span={7}
                                        style={{ textAlign: "right" }}
                                    >
                                        ${summary.original_price}
                                    </Col>
                                </Row>
                            )}

                            {summary.discounts !== "0.00" && (
                                <Row>
                                    <Col span={17}>Discount</Col>
                                    <Col
                                        span={7}
                                        style={{ textAlign: "right" }}
                                    >
                                        - ${summary.discounts}
                                    </Col>
                                </Row>
                            )}

                            {drawPromoLineItems(summary.promo_codes)}

                            <Divider style={{ margin: "10px 0" }} />

                            <Row>
                                <Col span={17}>Subtotal</Col>
                                <Col
                                    span={7}
                                    style={{ textAlign: "right" }}
                                >
                                    ${summary.subtotal}
                                </Col>
                            </Row>

                            {(summary.taxes || []).map(item => (
                                <Row key={`${item.type}-${item.amount}`}>
                                    <Col span={17}>
                                        <small>{item.type || "Tax"}</small>
                                    </Col>

                                    <Col
                                        span={7}
                                        style={{ textAlign: "right" }}
                                    >
                                        <small>${item.amount}</small>
                                    </Col>
                                </Row>
                            ))}

                            {summary.shipping !== "0.00" && (
                                <Row>
                                    <Col span={17}>
                                        <small>Shipping</small>
                                    </Col>

                                    <Col
                                        span={7}
                                        style={{ textAlign: "right" }}
                                    >
                                        <small>${summary.shipping}</small>
                                    </Col>
                                </Row>
                            )}

                            {drawPromoLineItems(summary.promo_codes, true)}

                            {summary.total_gift_card_amount_used !== "0.00" && (
                                <Row>
                                    <Col span={17}>Gift Cards</Col>

                                    <Col
                                        span={7}
                                        style={{ textAlign: "right" }}
                                    >
                                        -${summary.total_gift_card_amount_used}
                                    </Col>
                                </Row>
                            )}

                            <Divider style={{ margin: "10px 0 20px" }} />

                            <Row>
                                <Col span={17}>
                                    <strong style={{ fontSize: 18 }}>
                                        Total
                                    </strong>
                                </Col>

                                <Col
                                    span={7}
                                    style={{ textAlign: "right" }}
                                >
                                    <strong style={{ fontSize: 16 }}>
                                        ${summary.total}
                                    </strong>
                                </Col>
                            </Row>
                        </>
                    )}
                </Card>

                {!ismobile && drawPromoAndGC()}

            </div>
        );
    };


    const drawMobileSummary = () => {

        return (

            <div className="show-block mobile-summary-wrap" style={{ "position": "sticky", "top": (affixed) + "px", "margin": "-21px -20px", "marginBottom": "20px", "zIndex": "10", "backgroundColor": "#fff" }}>
                <div className="float-flex" style={{ "alignItems": "center", "padding": "10px 20px" }}>
                    <strong style={{ "fontSize": "18px" }}><small>Order Summary: {"$" + session.cart.summary.total}</small> </strong>
                    <div style={{ "display": "block", "fontSize": "12px" }}>{title_items}</div>
                </div>
            </div>

        )
    }


    // pickup label
    const getPickupText = () => {

        let _location_name = "";
        if (window.sitesettings.store_info.is_multistore && window.sitesettings.store_info.multistore_info.length > 0) {
            let _s = false;
            if (session.location_id) {
                _s = window.sitesettings.store_info.multistore_info.find(item => item.id == session.location_id);
            } else {

                _s = window.sitesettings.store_info.multistore_info[0];
            }
            _location_name = _s?.name;
            return `Pick up at: ${_location_name}`;
        } else {
            return "Pick up in store";
        }
    }


    const drawLabel = () => {

        if (status.delivery_preference === "electronic") {
            return "Email";
        }

        if (status.delivery_preference === "ship" || status.delivery_preference === "local_delivery") {

            if (status.custom && window.sitesettings.checkout_info.label_override_ship) {
                return window.sitesettings.checkout_info.label_custom_ship;
            }
            return "Ship";
        }

        if (status.delivery_preference === "pickup") {
            if (status.custom === true && window.sitesettings.checkout_info.label_override_pickup) {
                return window.sitesettings.checkout_info.label_custom_pickup;
            }
            return getPickupText()
        }

        return ""
    }

    const drawCheckout = () => {


        if (status.step !== STEPS.CHECKOUT) {
            return (<>{ismobile && drawPromoAndGC()}</>)
        }

        return (
            <>
                {/* {drawMobileSummary()} */}
                {/* SHIPPING */}
                {((status.delivery_preference === "ship" || status.delivery_preference === "local_delivery") && <div>
                    {drawBillingShipping()}

                    {/* <div className="show">
                        <Divider orientation="left" ><big><strong>Promo Codes</strong></big></Divider>

                        <PromoCodes />
                    </div> */}
                    <div className="shim" />
                    <Divider orientation="left" ><big><strong>Select a Shipping Option</strong></big></Divider>
                    {((status.delivery_preference === "ship" || status.delivery_preference === "local_delivery") &&
                        <div style={{ "height": "0px", "overflow": "hidden" }}>
                            <Form.Item style={{ "height": "0px", "overflow": "hidden" }} rules={[{ message: 'Shipping Required' }]} name="shipping_provider">
                                <Input tabIndex={-1} disabled aria-hidden="true" />
                            </Form.Item>
                            <Form.Item style={{ "height": "0px", "overflow": "hidden" }} rules={[{ required: true, message: 'Shipping Label' }]} name="shipping_label">

                                <Input tabIndex={-1} disabled aria-hidden="true" />
                            </Form.Item>
                        </div>
                    )}
                    {drawShipping()}
                    {errorMSG}

                    <Form.Item hidden name="multiship_method"><Input /></Form.Item>
                    <div className="shim" />
                </div>)}



                {drawNotice()}
                <div className="shim" />

                <Divider orientation="left" ><big><strong>Additional Information</strong></big></Divider>
                {drawContact()}

                {drawPayment()}
                {ismobile && drawPromoAndGC()}
                <Divider />

                {drawSubmit()}

                <div id="end"></div>

                {/* <pre>{JSON.stringify(status, null, 2)}</pre> */}
            </>
        );
    };



    if (!session?.cart?.items?.length) {
        return null;
    }


    return (
        <Spin indicator={<></>} spinning={submitLoading} >
            <div className="checkout-spin">
                <div className="container" style={{ padding: 0, backgroundColor: "transparent" }} >
                    <Form form={form} layout="vertical" onValuesChange={() => validateForm()} onFinish={submit} >
                        <Row gutter={0}>
                            <Col xs={24} sm={24} lg={15} style={{ minHeight: (ismobile) ? "auto" : "calc(100vh)" }} className="checkout-main-bg" >
                                <div style={{ padding: 20 }} >
                                    <Typography.Title style={{ "marginBottom": "20px" }} level={2}><nobr>Checkout</nobr></Typography.Title>

                                    {/* step 1 - email */}
                                    <PurchaseEmailChecker
                                        status={status} form={form}
                                        changeEmail={() => {
                                            form.setFieldsValue({
                                                payment_method: ""
                                            });

                                            setChoice("");
                                            setIsValid(false);
                                            setStatus(prev => ({
                                                ...prev,
                                                payment_method: "",
                                                step: STEPS.EMAIL
                                            }));

                                        }}
                                        callback={(emailAddress, new_user = false) => {
                                            if (emailAddress) { updateEmail(emailAddress, new_user); }
                                        }}
                                    />

                                    {/* step 2 - fulfillment */}
                                    <PurchaseFulfillmentSelector
                                        status={status}
                                        callback={updateFulfillment}
                                        changeFulfillment={() => {
                                            form.setFieldsValue({
                                                payment_method: ""
                                            });

                                            setChoice("");
                                            setIsValid(false);
                                            setStatus(prev => ({
                                                ...prev,
                                                payment_method: "",
                                                step: STEPS.FULFILLMENT
                                            }));
                                        }}
                                    />

                                      {drawShippingMessage()}
                                    {/* conditional guest shipping */}
                                    {!session.logged_in && status?.delivery_preference === "ship" && (status.step === STEPS.ADDITIONAL || status.step === STEPS.PAYMENT) && (
                                        <PurchaseGuestShipping
                                            form={form}
                                            status={status}
                                            callback={updateAdditionalInformation}
                                            changeAddress={() => {
                                                form.setFieldsValue({
                                                    payment_method: ""
                                                });
                                                setChoice("");
                                                setIsValid(false);
                                                setStatus(prev => ({
                                                    ...prev,
                                                    payment_method: "",
                                                    step: STEPS.ADDITIONAL
                                                }));
                                            }}

                                        />
                                    )}

                                  
                                    {drawLocalMessage()}
                                    {/* rest */}

                                    {drawCheckout()}


                                </div>

                            </Col>
                            <Col span={24} lg={9} className="checkout-summary-bg"  >

                                {drawSummary()}
                            </Col>
                        </Row>
                    </Form>
                </div>
            </div>
        </Spin>
    );
}